import React, { useEffect, useState } from 'react';
import { getFullStatus } from '../../services/BeholderService';
import { getAiUsage } from '../../services/SettingsService';

function fmtTokens(n) {
    n = Number(n) || 0;
    if (!n) return '0';
    if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
    if (n >= 1e3) return (n / 1e3).toFixed(0) + 'K';
    return String(n);
}

function fmtUptime(sec) {
    if (!sec) return '—';
    const d = Math.floor(sec / 86400);
    const h = Math.floor((sec % 86400) / 3600);
    const m = Math.floor((sec % 3600) / 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
}

function fmtAgoSec(sec) {
    if (sec === null || sec === undefined) return 'nunca';
    if (sec < 60) return `${sec}s atrás`;
    if (sec < 3600) return `${Math.floor(sec / 60)}min atrás`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h atrás`;
    return `${Math.floor(sec / 86400)}d atrás`;
}

function Dot({ color, tooltip, label }) {
    return (
        <span title={tooltip} style={{ cursor: 'default', whiteSpace: 'nowrap' }}>
            <span
                className={`d-inline-block rounded-circle me-1 ${color}`}
                style={{ width: 7, height: 7, verticalAlign: 'middle' }}
            />
            <span style={{ fontSize: 10 }}>{label}</span>
        </span>
    );
}

// Mini-tabela com a situação de cada IA (popover no hover do chip "N IAs"). Alimentada pelo
// providerHealth do getAiUsage (uso×limite×status por conta) + lastProvider do status.ai.
function AiPoolModal({ health, lastProvider, onClose }) {
    const rows = (health || []).slice().sort((a, b) => (b.useForTradeSignal ? 1 : 0) - (a.useForTradeSignal ? 1 : 0));
    if (rows.length === 0) return null;
    const statusColor = s => s === 'critical' ? '#dc3545' : s === 'warn' ? '#ffc107' : '#28a745';
    // Modal CENTRADO (não um popover preso à sidebar) → não corta e funciona no TOQUE (mobile, sem mouse).
    return (
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1080,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
        }}>
        <div onClick={e => e.stopPropagation()} style={{
            background: '#1f2937', border: '1px solid #374151', borderRadius: 8, padding: 12,
            minWidth: 260, maxWidth: 380, width: '100%', boxShadow: '0 8px 24px rgba(0,0,0,.6)', textAlign: 'left'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <strong style={{ fontSize: 13, color: '#e5e7eb' }}>IAs do pool · Distribuído</strong>
                <button onClick={onClose} className="btn-close btn-close-white" style={{ fontSize: 10 }} aria-label="Fechar" />
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6 }}>
                Cada IA cuida de um grupo de ativos (sharding).
            </div>
            <table style={{ width: '100%', fontSize: 10, color: '#e5e7eb', borderCollapse: 'collapse' }}>
                <tbody>
                    {rows.map(r => {
                        const used = (Number(r.usedDay) || 0);
                        const lim = r.dayLimit || 0;
                        const usage = lim > 0 ? `${fmtTokens(used)}/${fmtTokens(lim)} hoje` : `${fmtTokens(used)} hoje`;
                        return (
                            <tr key={r.id} style={{ opacity: r.useForTradeSignal ? 1 : 0.5 }}>
                                <td style={{ paddingRight: 5 }}>
                                    <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: statusColor(r.status) }} />
                                </td>
                                <td style={{ paddingRight: 6, whiteSpace: 'nowrap' }}>
                                    {r.provider}{r.provider === lastProvider ? ' ·última' : ''}
                                    {!r.useForTradeSignal ? <span style={{ color: '#9ca3af' }}> (fora do sinal)</span> : ''}
                                </td>
                                <td style={{ textAlign: 'right', color: '#9ca3af', whiteSpace: 'nowrap' }}>{usage}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
        </div>
    );
}

function SystemStatusBar() {
    const [status, setStatus] = useState(null);
    const [health, setHealth] = useState([]);
    const [aiModalOpen, setAiModalOpen] = useState(false);

    useEffect(() => {
        let alive = true;
        function load() {
            getFullStatus()
                .then(s => { if (alive) setStatus(s); })
                .catch(() => {});
            // providerHealth alimenta a mini-tabela do hover (uso×limite×status por IA). Best-effort.
            getAiUsage()
                .then(u => { if (alive) setHealth((u && u.providerHealth) || []); })
                .catch(() => {});
        }
        load();
        const id = setInterval(load, 60000);
        return () => { alive = false; clearInterval(id); };
    }, []);

    if (!status) return null;

    const { server, ai, exchange } = status;

    const serverColor = server.status === 'green' ? 'bg-success'
        : server.status === 'yellow' ? 'bg-warning' : 'bg-danger';
    const serverTip = `CPU ${server.load1Pct}% · RAM ${server.memPct}% · ${fmtUptime(server.uptimeSec)} no ar`;
    const serverLabel = `CPU ${server.load1Pct}% · RAM ${server.memPct}%`;

    const exchColor = exchange.ok === true ? (exchange.env === 'test' ? 'bg-warning' : 'bg-success')
        : exchange.ok === false ? 'bg-danger' : 'bg-secondary';
    const exchTip = exchange.ok
        ? `${exchange.exchangeName}${exchange.env === 'test' ? ' (testnet)' : ''} · ${exchange.latencyMs}ms`
        : exchange.ok === false
            ? `${exchange.exchangeName}: ${exchange.error}`
            : `${exchange.exchangeName}: verificando...`;
    const exchLabel = exchange.exchangeName || '—';

    const aiColor = !ai.provider ? 'bg-secondary'
        : !ai.ok ? 'bg-danger'
        : ai.isFallback ? 'bg-warning'
        : 'bg-success';
    const activeProvider = ai.isFallback ? ai.lastProvider : ai.provider;
    const lastTxt = ai.lastCallAgoSec !== null && ai.lastCallAgoSec !== undefined
        ? ` · última: ${ai.lastProvider || activeProvider} ${fmtAgoSec(ai.lastCallAgoSec)}` : '';
    let aiLabel, aiTip;
    if (ai.sharded) {
        // Modo Distribuído (sharding): mostra o POOL, não 1 provedor. Não é fallback — é o desenho.
        aiLabel = `${ai.providerCount} IAs`;
        aiTip = `Distribuído (sharding) entre ${ai.providerCount}: ${(ai.providers || []).join(', ')}${lastTxt}`;
    } else if (ai.ok && ai.isFallback) {
        aiLabel = `${activeProvider}*`;
        aiTip = `Primário: ${ai.provider} (esgotado) · Usando: ${ai.lastProvider} · ${ai.lastModel || ''}${ai.lastCallAgoSec !== null ? ` · ${fmtAgoSec(ai.lastCallAgoSec)}` : ''}`;
    } else if (ai.ok) {
        aiLabel = activeProvider || 'IA';
        aiTip = `${ai.provider} · ${ai.model}${ai.lastCallAgoSec !== null ? ` · última chamada ${fmtAgoSec(ai.lastCallAgoSec)}` : ''}`;
    } else {
        aiLabel = activeProvider || 'IA';
        aiTip = ai.error || 'IA não configurada';
    }

    return (
        <li className='nav-item mb-2 text-center'>
            <div className='sidebar-text d-flex justify-content-center align-items-center gap-2 flex-wrap'
                style={{ padding: '2px 8px' }}>
                <Dot color={exchColor} tooltip={exchTip} label={exchLabel} />
                {ai.sharded && health.length > 0 ? (
                    <span style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                        onClick={() => setAiModalOpen(true)} title="Ver as IAs do pool">
                        <Dot color={aiColor} tooltip={aiTip} label={aiLabel} />
                    </span>
                ) : (
                    <Dot color={aiColor} tooltip={aiTip} label={aiLabel} />
                )}
                <Dot color={serverColor} tooltip={serverTip} label={serverLabel} />
            </div>
            {exchange.ok === false && (
                <small className='sidebar-text d-block text-danger text-center'
                    style={{ fontSize: 9, maxWidth: 160, margin: '0 auto', wordBreak: 'break-word' }}>
                    {exchange.error?.slice(0, 50)}
                </small>
            )}
            {ai.error && ai.provider === null && (
                <small className='sidebar-text d-block text-muted text-center' style={{ fontSize: 9 }}>
                    {ai.error}
                </small>
            )}
            {aiModalOpen && (
                <AiPoolModal health={health} lastProvider={ai.lastProvider} onClose={() => setAiModalOpen(false)} />
            )}
        </li>
    );
}

export default SystemStatusBar;
